import type { Area } from 'react-easy-crop'

/** Final output edge length for cropped logos — see SLICE-4a-2 output spec. */
export const LOGO_OUTPUT_SIZE = 600
/** Source images larger than this on either side are down-sampled before crop. */
export const SOURCE_MAX_DIMENSION = 2048
/** canvas.toBlob quality for the PNG output. */
export const LOGO_OUTPUT_QUALITY = 0.92

/**
 * Load a source File into an HTMLImageElement. `react-easy-crop` applies EXIF
 * orientation itself for the on-screen canvas; modern browsers also bake EXIF
 * orientation into decoded `<img>` bitmaps, so the crop area coordinates and
 * this draw stay in agreement. Verify on a real Android photo before merge.
 */
function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Could not load the selected image.'))
    }
    img.src = url
  })
}

/**
 * Down-sample a source image to <= SOURCE_MAX_DIMENSION on its longest side.
 * Returns the original element untouched when it's already small enough — no
 * point spending a canvas pass. Keeps 12MP phone photos from holding 12MB+ of
 * decoded bitmap around on low-end Android.
 */
function downsampleSource(
  img: HTMLImageElement,
): HTMLImageElement | HTMLCanvasElement {
  const longest = Math.max(img.naturalWidth, img.naturalHeight)
  if (longest <= SOURCE_MAX_DIMENSION) return img

  const scale = SOURCE_MAX_DIMENSION / longest
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(img.naturalWidth * scale)
  canvas.height = Math.round(img.naturalHeight * scale)
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Could not process the image.')
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
  return canvas
}

/**
 * Crop `file` to the `pixelCrop` area (in source-image pixels, as reported by
 * react-easy-crop's `onCropComplete`), scale the result to `outputSize` square,
 * and encode it as a PNG Blob.
 *
 * The crop area comes from react-easy-crop measured against the *original*
 * image dimensions. When we down-sample the source first, we scale the crop
 * rect by the same factor so it still points at the right region.
 *
 * `outputSize` defaults to LOGO_OUTPUT_SIZE (600) — the logo crop. Slice 4a-3's
 * custom stamp-icon crop passes 200 (STAMP_ICON_DIMENSION).
 */
export async function cropToSquareBlob(
  file: File,
  pixelCrop: Area,
  outputSize: number = LOGO_OUTPUT_SIZE,
): Promise<Blob> {
  const img = await loadImage(file)
  const source = downsampleSource(img)

  const sourceW = source instanceof HTMLCanvasElement ? source.width : source.naturalWidth
  const factor = sourceW / img.naturalWidth

  const canvas = document.createElement('canvas')
  canvas.width = outputSize
  canvas.height = outputSize
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Could not process the image.')
  ctx.imageSmoothingQuality = 'high'

  ctx.drawImage(
    source,
    pixelCrop.x * factor,
    pixelCrop.y * factor,
    pixelCrop.width * factor,
    pixelCrop.height * factor,
    0,
    0,
    outputSize,
    outputSize,
  )

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob)
        else reject(new Error('Could not process the image.'))
      },
      'image/png',
      LOGO_OUTPUT_QUALITY,
    )
  })
}

/** Logo-specific crop — 600×600 PNG. Thin alias kept for call-site clarity. */
export function cropToLogoBlob(file: File, pixelCrop: Area): Promise<Blob> {
  return cropToSquareBlob(file, pixelCrop, LOGO_OUTPUT_SIZE)
}

/**
 * Rectangular crop variant — same shape as cropToSquareBlob but accepts a
 * non-square output (`outputW × outputH`). Used by the hero photo crop
 * (1032×336, 3:1). The crop frame is set via react-easy-crop's `aspect` prop
 * to match outputW/outputH so the area pixels come back rectangular.
 */
export async function cropToRectBlob(
  file: File,
  pixelCrop: Area,
  outputW: number,
  outputH: number,
): Promise<Blob> {
  const img = await loadImage(file)
  const source = downsampleSource(img)
  const sourceW =
    source instanceof HTMLCanvasElement ? source.width : source.naturalWidth
  const factor = sourceW / img.naturalWidth

  const canvas = document.createElement('canvas')
  canvas.width = outputW
  canvas.height = outputH
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Could not process the image.')
  ctx.imageSmoothingQuality = 'high'

  ctx.drawImage(
    source,
    pixelCrop.x * factor,
    pixelCrop.y * factor,
    pixelCrop.width * factor,
    pixelCrop.height * factor,
    0,
    0,
    outputW,
    outputH,
  )

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob)
        else reject(new Error('Could not process the image.'))
      },
      'image/png',
      LOGO_OUTPUT_QUALITY,
    )
  })
}
